import type { ApiClient } from "@/api/ApiClient";
import { type ApiResult, isSuccess } from "@/api/ApiResult";
import { assertStateIsLoaded } from "@/features/data_entry/utils/utils";
import type { DataEntry, DataEntryStatusResponse, Results, SaveDataEntryResponse } from "@/types/generated/openapi";
import { type DataEntryResults, dataEntryModelValues, type FormSectionId, type SectionValues } from "@/types/types";
import { mapSectionValues } from "@/utils/dataEntryMapping";
import { isRecord } from "@/utils/typeChecks";

import type {
  DataEntryDispatch,
  DataEntryState,
  FormSection,
  SubmitCurrentFormOptions,
  TemporaryCache,
} from "../types/types";
import { calculateDataEntryProgress, getClientState, resetDisabledSectionValues } from "./dataEntryUtils";

function isResults(value: DataEntryResults): value is Results {
  if (!isRecord(value)) {
    return false;
  }

  const model = value.model;
  return typeof model === "string" && dataEntryModelValues.some((v) => v === model);
}

export function setCache(dispatch: DataEntryDispatch) {
  return (cache: TemporaryCache) => {
    dispatch({ type: "SET_CACHE", cache });
  };
}

export function updateFormSection(dispatch: DataEntryDispatch) {
  return (sectionId: FormSectionId, partialFormSection: Partial<FormSection>) => {
    dispatch({ type: "UPDATE_FORM_SECTION", sectionId, partialFormSection });
  };
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: TODO function should be refactored
export function onSubmitForm(
  client: ApiClient,
  requestPath: string,
  dispatch: DataEntryDispatch,
  state: DataEntryState,
) {
  return async (
    sectionId: FormSectionId,
    currentValues: SectionValues,
    {
      aborting = false,
      continueToNextSection = true,
      showAcceptErrorsAndWarnings = true,
    }: SubmitCurrentFormOptions = {},
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO function should be refactored
  ): Promise<boolean> => {
    assertStateIsLoaded(state);
    const currentSection = state.formState.sections[sectionId];

    if (!currentSection) {
      return false;
    }

    if (
      !aborting &&
      (!currentSection.errors.isEmpty() || !currentSection.warnings.isEmpty()) &&
      showAcceptErrorsAndWarnings &&
      !currentSection.acceptErrorsAndWarnings
    ) {
      dispatch({
        type: "UPDATE_FORM_SECTION",
        sectionId: sectionId,
        partialFormSection: { acceptErrorsAndWarningsError: true },
      });
      return false;
    }

    const dataEntrySection = state.dataEntryStructure.find((s) => s.id === currentSection.id);
    if (!dataEntrySection || !isResults(state.results)) {
      return false;
    }

    let data = mapSectionValues(state.results, currentValues, dataEntrySection);
    if (aborting && state.cache) {
      const cache = state.cache;
      const cachedDataEntrySection = state.dataEntryStructure.find((s) => s.id === cache.key);
      if (cachedDataEntrySection) {
        data = mapSectionValues(data, cache.data, cachedDataEntrySection);
      }
    }

    resetDisabledSectionValues(state.dataEntryStructure, data);

    // prepare data to send to server
    const clientState = getClientState(
      state.formState,
      sectionId,
      currentSection.acceptErrorsAndWarnings,
      continueToNextSection,
    );
    const progress = calculateDataEntryProgress(state.formState);

    // send data to server
    dispatch({ type: "SET_STATUS", status: "saving", sectionId });

    const response: ApiResult<SaveDataEntryResponse> = await client.postRequest(requestPath, {
      progress,
      data,
      client_state: clientState,
    } satisfies DataEntry);

    dispatch({ type: "SET_STATUS", status: aborting ? "aborted" : "idle" });

    if (!isSuccess(response)) {
      dispatch({ type: "FORM_SAVE_FAILED", error: response });
      return false;
    }

    // handle validation errors
    dispatch({
      type: "FORM_SAVED",
      data,
      validationResults: response.data.validation_results,
      sectionId,
      aborting,
      continueToNextSection,
    });

    return true;
  };
}

export function onDiscardDataEntry(client: ApiClient, requestPath: string, dispatch: DataEntryDispatch) {
  return async (): Promise<boolean> => {
    dispatch({ type: "SET_STATUS", status: "discarding" });

    const response = await client.deleteRequest(requestPath);

    if (!isSuccess(response)) {
      dispatch({ type: "SET_STATUS", status: "idle" });
      dispatch({ type: "FORM_SAVE_FAILED", error: response });
      return false;
    }

    dispatch({ type: "SET_STATUS", status: "discarded" });
    return true;
  };
}

export function onFinaliseDataEntry(client: ApiClient, requestPath: string, dispatch: DataEntryDispatch) {
  return async (): Promise<DataEntryStatusResponse | undefined> => {
    dispatch({ type: "SET_STATUS", status: "finalising" });

    const response = await client.postRequest<DataEntryStatusResponse>(requestPath);

    if (!isSuccess(response)) {
      dispatch({ type: "SET_STATUS", status: "idle" });
      dispatch({ type: "FORM_SAVE_FAILED", error: response });
      return undefined;
    }

    dispatch({ type: "SET_STATUS", status: "finalised" });
    return response.data;
  };
}
